/**
 * useSyncUser — keep the signed-in user's `app_users` row current, cheaply.
 *
 * Replaces the Clerk webhook as the "new + ongoing users" path (existing users are seeded once
 * by scripts/backfill-app-users.mjs). It calls the `lb_sync_me()` RPC, which upserts the user's
 * own row from their verified Clerk JWT (see db/collaboration/13_sync_me.sql).
 *
 * Efficiency: this does NOT hit the DB on every load. A localStorage marker records that this
 * user was synced on this device; we only call the RPC when the marker is absent (new user / new
 * device), stale (older than TTL — lets name/avatar changes trickle in), or from an older version.
 * So the cost is ~one RPC per user per device per week, and exactly one for a brand-new user.
 */

import { useEffect } from 'react'
import { supabase } from './supabase'
import { useTenantWithUser } from './tenant'

const MARKER_KEY = 'lb_user_synced'
const TTL_MS = 7 * 24 * 60 * 60 * 1000 // re-sync at most weekly
const VERSION = 1                       // bump to force a one-off re-sync for everyone

function recentlySynced(userId: string): boolean {
  try {
    const raw = localStorage.getItem(MARKER_KEY)
    if (!raw) return false
    const [v, uid, ts] = raw.split(':')
    return Number(v) === VERSION && uid === userId && Date.now() - Number(ts) < TTL_MS
  } catch {
    return false
  }
}

function markSynced(userId: string) {
  try { localStorage.setItem(MARKER_KEY, `${VERSION}:${userId}:${Date.now()}`) } catch { /* ignore */ }
}

/** Mount once near the app root. No-op until Clerk resolves a signed-in user. */
export function useSyncUser() {
  const { userId, isLoaded } = useTenantWithUser()

  useEffect(() => {
    if (!isLoaded || !userId) return
    if (recentlySynced(userId)) return // skip the DB entirely on repeat loads

    let cancelled = false
    supabase.rpc('lb_sync_me').then(({ error }) => {
      if (cancelled) return
      if (error) { console.error('[syncUser] lb_sync_me failed:', error.message); return }
      markSynced(userId)
    })
    return () => { cancelled = true }
  }, [userId, isLoaded])
}
